import { useEffect, useState } from 'react';
import { Plural, Trans, useLingui } from '@lingui/react/macro';

import Message from '@/design_system/Message';
import Stack from '@/design_system/Stack';
import Paragraph from '@/design_system/Typography/Paragraph';
import {
  useBulkRepair,
  useBulkStartExchange,
  useBulkStartRepair,
  useBulkValidateTransit,
} from '@/models/article';
import {
  type BatchResultResponse,
  type RequestWithRelationsFromRequestsList,
} from '@/models/request';
import { TrackingNumberInput } from '@/routes/Requests/Request/Article/components/ArticleActions/shared/TrackingNumberInput';

import { type BulkableTaskType, type BulkTaskScope } from '../BulkActionBar/getBulkEligibility';
import { getBulkTaskLabel } from '../BulkActionBar/getBulkTaskLabel';

import { BulkConfirmationDialog } from './BulkConfirmationDialog';

export const BulkTaskDialog = ({
  showDialog,
  setShowDialog,
  taskType,
  scope,
  selectedRequests,
  onSuccess,
}: {
  showDialog: boolean;
  setShowDialog: (show: boolean) => void;
  taskType: BulkableTaskType | null;
  scope: BulkTaskScope | null;
  selectedRequests: RequestWithRelationsFromRequestsList[];
  onSuccess: () => void;
}) => {
  const { i18n } = useLingui();
  const [trackingNumber, setTrackingNumber] = useState<string | undefined>(undefined);
  const { mutateAsync: validateTransit, isPending: isValidateTransitPending } =
    useBulkValidateTransit();
  const { mutateAsync: startRepair, isPending: isStartRepairPending } = useBulkStartRepair();
  const { mutateAsync: startExchange, isPending: isStartExchangePending } = useBulkStartExchange();
  const { mutateAsync: repairArticles, isPending: isRepairPending } = useBulkRepair();

  const isPending =
    isValidateTransitPending || isStartRepairPending || isStartExchangePending || isRepairPending;
  const articleIds = scope?.articleIds ?? [];
  const itemCount = articleIds.length;
  const skippedCount = selectedRequests.length - (scope?.eligibleRequests.length ?? 0);

  useEffect(() => {
    if (showDialog) {
      setTrackingNumber(undefined);
    }
  }, [showDialog]);

  if (!taskType || !scope) {
    return null;
  }

  const taskLabel = getBulkTaskLabel(taskType, i18n);

  return (
    <BulkConfirmationDialog
      title={taskLabel}
      confirm={
        taskType === 'validate_transit' ? (
          <Trans id="article.validate-transit-modal.actions.validate">Validate</Trans>
        ) : (
          <Trans id="requests.bulk.task.confirm">Confirm</Trans>
        )
      }
      showDialog={showDialog}
      setShowDialog={setShowDialog}
      isPending={isPending}
      isConfirmDisabled={articleIds.length === 0}
      maxWidth="32rem"
      requests={selectedRequests}
      onCloseAfterResult={onSuccess}
      onConfirm={async (): Promise<BatchResultResponse> => {
        const body = { articleIds };

        if (taskType === 'validate_transit') {
          return validateTransit({
            ...body,
            trackingNumber: trackingNumber?.trim() || undefined,
          });
        }

        if (taskType === 'start_article_repair') {
          return startRepair(body);
        }

        if (taskType === 'start_article_exchange') {
          return startExchange(body);
        }

        return repairArticles(body);
      }}
    >
      <Stack gap="1rem">
        <Stack gap="0.5rem">
          <Paragraph>
            <BulkTaskScopeLine taskType={taskType} itemCount={itemCount} />
          </Paragraph>
          {skippedCount > 0 && (
            <Message type="neutral">
              <BulkTaskSkipLine taskType={taskType} skippedCount={skippedCount} />
            </Message>
          )}
        </Stack>
        {taskType === 'validate_transit' && (
          <TrackingNumberInput
            trackingNumber={trackingNumber}
            setTrackingNumber={setTrackingNumber}
          />
        )}
      </Stack>
    </BulkConfirmationDialog>
  );
};

const BulkTaskScopeLine = ({
  taskType,
  itemCount,
}: {
  taskType: BulkableTaskType;
  itemCount: number;
}) => {
  switch (taskType) {
    case 'start_article_repair':
      return (
        <Plural
          id="requests.bulk.task.start-repair.scope"
          value={itemCount}
          one="You'll start the repair on # item"
          other="You'll start the repair on # items"
        />
      );
    case 'start_article_exchange':
      return (
        <Plural
          id="requests.bulk.task.start-exchange.scope"
          value={itemCount}
          one="You'll start the exchange on # item"
          other="You'll start the exchange on # items"
        />
      );
    case 'repair_article':
      return (
        <Plural
          id="requests.bulk.task.repair.scope"
          value={itemCount}
          one="You'll mark # item as repaired"
          other="You'll mark # items as repaired"
        />
      );
    case 'validate_transit':
      return (
        <Plural
          id="requests.bulk.task.validate-transit.scope"
          value={itemCount}
          one="You'll validate transit for # item"
          other="You'll validate transit for # items"
        />
      );
  }
};

const BulkTaskSkipLine = ({
  taskType,
  skippedCount,
}: {
  taskType: BulkableTaskType;
  skippedCount: number;
}) => {
  switch (taskType) {
    case 'start_article_repair':
      return (
        <Plural
          id="requests.bulk.task.start-repair.skip"
          value={skippedCount}
          one="# request has no item to start the repair on"
          other="# requests have no item to start the repair on"
        />
      );
    case 'start_article_exchange':
      return (
        <Plural
          id="requests.bulk.task.start-exchange.skip"
          value={skippedCount}
          one="# request has no item to start the exchange on"
          other="# requests have no item to start the exchange on"
        />
      );
    case 'repair_article':
      return (
        <Plural
          id="requests.bulk.task.repair.skip"
          value={skippedCount}
          one="# request has no item to mark as repaired"
          other="# requests have no item to mark as repaired"
        />
      );
    case 'validate_transit':
      return (
        <Plural
          id="requests.bulk.task.validate-transit.skip"
          value={skippedCount}
          one="# request has no item to validate transit for"
          other="# requests have no item to validate transit for"
        />
      );
  }
};
